/**
 * The full-screen clock-in / clock-out flow:
 *   (privacy notice, first time only) → scan the sticker → wait briefly for GPS → send → result.
 *
 * GPS starts as soon as the flow opens (indoor fixes are slow), alongside the camera, and
 * is kept going (or restarted) after the scan if it hasn't found a usable fix yet.
 * One clientId is used for the whole flow, so retries, "Request approval" and a punch
 * that is saved on the phone after a timeout can never create two visits. If the phone
 * has no signal (or the server doesn't answer within 20 s) the punch is saved and sent later.
 */
import { type ReactNode, useEffect, useRef, useState } from "react";
import type { Me, PunchRequest, PunchResponse } from "../../shared/types";
import { Spinner } from "../components/ui";
import { ApiError } from "../lib/api";
import { fmtDuration, fmtTime } from "../lib/format";
import {
  acknowledgeLocationNotice,
  GEO_DENIED,
  type GeoSnapshot,
  LocationCapture,
  locationAllowedBefore,
  locationNoticeAcknowledged,
  MAX_WATCH_MS,
  POOR_ACCURACY_M,
  punchLocation,
  rememberLocationAllowed,
  SEND_NOW_ACCURACY_M,
} from "../lib/geo";
import {
  belongsTo,
  discardQueued,
  dismissFlushNotes,
  type FlushResult,
  type FlushStop,
  flushQueue,
  isNetworkError,
  listQueued,
  type PunchKind,
  queuePunch,
  sendPunch,
} from "../lib/offlineQueue";
import {
  CameraError,
  type CameraProblem,
  getQrDetector,
  openRearCamera,
  parseClockCode,
  resetQrDetector,
  ScannerError,
  scanVideo,
  stopStream,
} from "../lib/scanner";
import { markPunchFlowOpen, rememberStickerSite, stickerSiteName } from "./flowState";
import { CameraHelp, LocationHelp, LocationPermissionStep, type LocationProblem } from "./help";
import { joinNotes, MAX_NOTE, plainFlags } from "./wording";

export type FlowMode = "scan" | "manual" | "preset";

type Blocked = Extract<PunchResponse, { status: "blocked" }>;
type Answered = Exclude<PunchResponse, { status: "blocked" }>;

/** Why a punch ended up saved on the phone instead of sent. */
type SavedWhy =
  | "offline" // the phone says it has no signal
  | "network" // the request failed to get through
  | "timeout" // no answer within PUNCH_TIMEOUT_MS
  | "cancelled" // the cleaner stopped waiting
  | "stuck" // an earlier saved punch can't be sent yet, and this one has to wait behind it
  | "server"; // this saved punch reached the server, which couldn't take it yet (5xx, 429…)

/** "Request failed (503)" → "Request failed (503)." so the next sentence reads properly. */
function asSentence(text: string): string {
  const t = text.trim();
  return /[.!?]$/.test(t) ? t : `${t}.`;
}

/** Turns the reason a flush stopped into what the "saved" screen should say. */
function savedWhy(stop: FlushStop | null, clientId: string): SavedWhy {
  if (!stop || stop.reason === "offline") return "offline";
  if (stop.reason === "network") return "network";
  return stop.item && stop.item.clientId === clientId ? "server" : "stuck";
}

type Step =
  /** Ask for location with a button tap first, so the phone reliably shows its prompt. */
  | { name: "permission"; phase: "intro" | "waiting" | "denied" }
  | { name: "scan" }
  | { name: "manual" }
  | { name: "locating" }
  | { name: "sending"; startedAt: number }
  | { name: "result"; response: Answered }
  | { name: "blocked"; response: Blocked }
  | { name: "queued"; at: number; why: SavedWhy; stuck: FlushStop | null; discarded?: boolean }
  | { name: "error"; message: string; canRetry: boolean };

/** What the cleaner is clocking against, fixed at the moment of the scan / tap. */
interface Target {
  qr: string | null;
  siteId: string | null;
  note: string | null;
  capturedAt: number;
}

const WAIT_AFTER_SCAN_MS = 8_000;
/** With a sticker opened from the phone's camera, GPS only starts now, so allow a little longer. */
const WAIT_PRESET_MS = 12_000;
const IDLE_GEO: GeoSnapshot = { fix: null, errorCode: null, finished: false };

export function PunchFlow({
  kind,
  me,
  mode,
  presetToken,
  onClose,
}: {
  kind: PunchKind;
  me: Me;
  mode: FlowMode;
  presetToken?: string | null;
  /** `changed` is true when the server (or the offline queue) now has a new punch. */
  onClose: (changed: boolean) => void;
}) {
  const verb = kind === "in" ? "Clock in" : "Clock out";
  const what = kind === "in" ? "clock-in" : "clock-out";
  const firstStep = (): Step =>
    mode === "scan" ? { name: "scan" } : mode === "manual" ? { name: "manual" } : { name: "locating" };

  // Until this phone has given us a location once, start with the "Allow location" step:
  // its button asks for location from the tap itself, before the camera opens, which is
  // when phones (iPhones especially) reliably show their permission prompt.
  const needsPermissionStep = () => !(locationNoticeAcknowledged() && locationAllowedBefore());
  const [step, setStepState] = useState<Step>(() =>
    needsPermissionStep() ? { name: "permission", phase: "intro" } : firstStep(),
  );
  const [clientId] = useState(() => crypto.randomUUID());
  const targetRef = useRef<Target | null>(
    mode === "preset" && presetToken
      ? { qr: presetToken, siteId: null, note: null, capturedAt: Date.now() }
      : null,
  );
  const lastReqRef = useRef<PunchRequest | null>(null);
  const changedRef = useRef(false);
  const mountedRef = useRef(true);
  /** The request being sent now, so "Stop waiting" (or closing) can abort it and save it. */
  const sendingRef = useRef<{ ctrl: AbortController; req: PunchRequest } | null>(null);

  const setStep = (s: Step) => {
    if (mountedRef.current) setStepState(s);
  };

  // While the flow is open, an app update must not reload the page (see main.tsx).
  useEffect(() => {
    mountedRef.current = true;
    const closed = markPunchFlowOpen();
    return () => {
      mountedRef.current = false;
      closed();
    };
  }, []);

  // --- Location: starts with the flow when it was allowed before; otherwise from the
  // "Allow location" button. Restarts on "Try again".
  const [geoRun, setGeoRun] = useState(() => (needsPermissionStep() ? 0 : 1));
  const geoRef = useRef<LocationCapture | null>(null);
  const geoOffRef = useRef<(() => void) | null>(null);
  const [geo, setGeo] = useState<GeoSnapshot>(IDLE_GEO);
  /** (Re)starts location capture. Call it straight from a tap where possible. */
  const startGeo = () => {
    geoOffRef.current?.();
    geoRef.current?.stop();
    const g = new LocationCapture();
    geoRef.current = g;
    geoOffRef.current = g.subscribe(setGeo);
    g.start();
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: restarts only when geoRun changes
  useEffect(() => {
    if (geoRun > 0) startGeo();
  }, [geoRun]);
  useEffect(
    () => () => {
      geoOffRef.current?.();
      geoRef.current?.stop();
    },
    [],
  );

  // Remember whether this phone allows location, so the next clock-in can skip the step.
  useEffect(() => {
    if (geo.fix) rememberLocationAllowed(true);
    else if (geo.errorCode === GEO_DENIED) rememberLocationAllowed(false);
  }, [geo.fix, geo.errorCode]);

  // On the "Allow location" step: move on once the phone has answered.
  const permissionPhase = step.name === "permission" ? step.phase : null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: reacts to location answers only
  useEffect(() => {
    if (permissionPhase !== "waiting") return;
    if (geo.fix) {
      setStep(firstStep());
    } else if (geo.errorCode === GEO_DENIED) {
      setStep({ name: "permission", phase: "denied" });
    } else if (geo.errorCode !== null || geo.finished) {
      // Allowed, but no position yet (indoors, say): carry on; GPS keeps trying while they scan.
      setStep(firstStep());
    }
  }, [permissionPhase, geo]);
  // Allowed but the first position is slow to arrive: don't keep them waiting on this screen.
  // biome-ignore lint/correctness/useExhaustiveDependencies: one timer per entry into "waiting"
  useEffect(() => {
    if (permissionPhase !== "waiting") return;
    const t = setTimeout(async () => {
      if (geoRef.current?.snapshot.errorCode === GEO_DENIED) return;
      // Only move on if the phone isn't still showing its prompt.
      const state = await navigator.permissions
        ?.query({ name: "geolocation" })
        .then((p) => p.state)
        .catch(() => "unknown");
      if (state !== "prompt" && mountedRef.current) setStep(firstStep());
    }, 6_000);
    return () => clearTimeout(t);
  }, [permissionPhase]);

  /** "Allow location" tapped: ask the phone now, from this tap. */
  const allowLocation = () => {
    acknowledgeLocationNotice();
    startGeo();
    setStep({ name: "permission", phase: "waiting" });
  };

  // Back from the phone's Settings (where the help sends them to allow location): try again.
  const stepName = step.name;
  useEffect(() => {
    if (stepName !== "scan" && stepName !== "manual" && stepName !== "locating") return;
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      const snap = geoRef.current?.snapshot;
      if (snap?.finished && !(snap.fix && snap.fix.accuracy <= POOR_ACCURACY_M)) {
        geoRef.current?.keepWatching(MAX_WATCH_MS);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [stepName]);

  // Keep the page behind from scrolling while the flow is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const siteNameForQueue = (t: Target): string | null => {
    if (kind === "out") return me.openVisit?.siteName ?? null;
    if (t.siteId) return me.sites.find((s) => s.id === t.siteId)?.name ?? null;
    return t.qr ? stickerSiteName(t.qr) : null;
  };

  async function saveForLater(req: PunchRequest, t: Target, why: SavedWhy, stuck: FlushStop | null = null) {
    try {
      await queuePunch(kind, req, {
        siteName: siteNameForQueue(t),
        userId: me.user.id,
        capturedAt: t.capturedAt,
      });
      changedRef.current = true;
      setStep({ name: "queued", at: t.capturedAt, why, stuck });
    } catch {
      setStep({
        name: "error",
        message: "No signal, and this phone couldn't save it either. Please try again when you have signal.",
        canRetry: true,
      });
    }
  }

  /** Saves the punch being sent now (it may or may not have reached the server). */
  function stopWaiting() {
    const sending = sendingRef.current;
    const t = targetRef.current;
    if (!sending || !t) return;
    sendingRef.current = null;
    sending.ctrl.abort();
    void saveForLater(sending.req, t, "cancelled");
  }

  // Closing the flow (or leaving the page) mid-send: keep the punch rather than lose it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: unmount only; stopWaiting reads refs
  useEffect(() => () => stopWaiting(), []);

  const close = () => {
    if (sendingRef.current) {
      stopWaiting();
      return; // the "saved on this phone" screen explains what happened
    }
    onClose(changedRef.current);
  };

  function showResult(res: PunchResponse, t: Target) {
    if (res.status === "blocked") {
      setStep({ name: "blocked", response: res });
      return;
    }
    if (res.status !== "rejected") {
      changedRef.current = true;
      if (t.qr) rememberStickerSite(t.qr, res.visit.siteName);
    }
    setStep({ name: "result", response: res });
  }

  async function deliver(req: PunchRequest) {
    const t = targetRef.current!;
    const ctrl = new AbortController();
    sendingRef.current = { ctrl, req };
    // Once "Stop waiting" has taken over (and saved the punch), this run must not touch the screen.
    const stillMine = () => sendingRef.current?.ctrl === ctrl;
    const finish = () => {
      if (stillMine()) sendingRef.current = null;
    };
    setStep({ name: "sending", startedAt: Date.now() });
    try {
      if (navigator.onLine === false) {
        finish();
        return await saveForLater(req, t, "offline");
      }
      // Anything already waiting on this phone must go first (a clock-in before its clock-out).
      const waiting = (await listQueued()).filter(
        (i) => belongsTo(i, me.user.id) && i.clientId !== req.clientId,
      );
      if (waiting.length) {
        const flushed = await flushQueue();
        if (!stillMine()) return;
        if (flushed.remaining > 0) {
          finish();
          const stop = flushed.stopped;
          const why = savedWhy(stop, clientId);
          return await saveForLater(req, t, why, why === "stuck" || why === "server" ? stop : null);
        }
      }
      const res = await sendPunch(kind, req, { signal: ctrl.signal });
      if (!stillMine()) return;
      finish();
      showResult(res, t);
    } catch (e) {
      if (!stillMine()) return;
      finish();
      if (isNetworkError(e)) {
        return await saveForLater(req, t, (e as ApiError).code === "timeout" ? "timeout" : "network");
      }
      if (e instanceof ApiError) {
        // 409: already clocked in / not clocked in — the screen behind needs refreshing.
        if (e.status === 409) changedRef.current = true;
        setStep({ name: "error", message: e.message, canRetry: e.status >= 500 || e.status === 429 });
      } else {
        setStep({ name: "error", message: "Something went wrong. Please try again.", canRetry: true });
      }
    }
  }

  /** "Try sending now" on the saved screen: send what's on the phone, this punch included. */
  async function sendSavedNow() {
    const t = targetRef.current;
    if (!t) return;
    setStep({ name: "sending", startedAt: Date.now() });
    const r: FlushResult = await flushQueue();
    const mine = r.outcomes.find((o) => o.item.clientId === clientId);
    if (mine) dismissFlushNotes(me.user.id, [clientId]); // shown here, no need for the banner too
    if (mine?.response) {
      showResult(mine.response, t);
    } else if (mine?.error) {
      if (mine.error.status === 409) changedRef.current = true;
      setStep({ name: "error", message: mine.error.message, canRetry: false });
    } else {
      const stop = r.stopped;
      const why = savedWhy(stop, clientId);
      setStep({
        name: "queued",
        at: t.capturedAt,
        why,
        stuck: why === "stuck" || why === "server" ? stop : null,
      });
    }
  }

  /** "Remove the earlier one": drop the stuck punch, then try again with this one. */
  async function discardStuck(stuck: FlushStop) {
    if (!stuck.item) return;
    await discardQueued(stuck.item.clientId).catch(() => {});
    changedRef.current = true;
    await sendSavedNow();
  }

  /** "Don't send it": drop this punch from the phone. */
  async function discardThis() {
    await discardQueued(clientId).catch(() => {});
    if (step.name === "queued") setStep({ ...step, discarded: true });
  }

  /** Builds the request from the target and the best location so far, and sends it. */
  function submit() {
    const t = targetRef.current;
    if (!t) return;
    const snap = geoRef.current?.snapshot ?? { ...IDLE_GEO, finished: true };
    geoRef.current?.stop(); // done with location for this punch
    const req: PunchRequest = {
      clientId,
      qr: t.qr,
      siteId: t.siteId,
      note: t.note,
      ...punchLocation(snap),
    };
    lastReqRef.current = req;
    void deliver(req);
  }

  function requestApproval(blocked: Blocked, note: string) {
    const prev = lastReqRef.current;
    if (!prev) return;
    const req: PunchRequest = {
      ...prev,
      requestApproval: true,
      // Lets the server keep the time of the blocked attempt, not the time of this tap.
      attemptToken: blocked.attemptToken,
      // Keep what they wrote on "Can't scan" as well as what they write now.
      note: joinNotes(prev.note, note),
    };
    lastReqRef.current = req;
    void deliver(req);
  }

  function retryScan() {
    targetRef.current = null;
    lastReqRef.current = null;
    setGeoRun((n) => n + 1);
    setStep({ name: "scan" });
  }

  function goManual() {
    geoRef.current?.keepWatching(MAX_WATCH_MS);
    setStep({ name: "manual" });
  }

  // --- After a scan: wait a little for a usable GPS fix, then send.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs once per entry into "locating"
  useEffect(() => {
    if (stepName !== "locating") return;
    let cancelled = false;
    const g = geoRef.current;
    const wait = mode === "preset" ? WAIT_PRESET_MS : WAIT_AFTER_SCAN_MS;
    // The scan may have come long after the flow opened: if GPS has given up (or would
    // give up before the wait is over) without a good fix, keep it going for the wait.
    const fix = g?.snapshot.fix;
    if (g && !(fix && fix.accuracy <= SEND_NOW_ACCURACY_M)) g.keepWatching(wait);
    void (g ? g.waitForFix(wait) : Promise.resolve()).then(() => {
      if (!cancelled) submit();
    });
    return () => {
      cancelled = true;
    };
  }, [stepName]);

  // --- Header
  const closeLabel =
    step.name === "result" || step.name === "queued"
      ? "Close"
      : step.name === "sending"
        ? "Stop waiting"
        : "Cancel";
  const header = (
    <div className="cl-flow-bar">
      <button className="btn ghost cl-flow-close" onClick={close}>
        {closeLabel}
      </button>
      <span className="cl-flow-title">{verb}</span>
      <span className="cl-flow-spacer" />
    </div>
  );

  let body: ReactNode;
  switch (step.name) {
    case "permission":
      body = (
        <LocationPermissionStep
          phase={step.phase}
          onAllow={allowLocation}
          onSkip={() => {
            // Carry on without location: the office decides (it may need approval).
            if (!geoRef.current) startGeo();
            setStep(firstStep());
          }}
          onCancel={close}
        />
      );
      break;
    case "scan":
      body = (
        <ScanView
          geo={geo}
          onCode={(token) => {
            targetRef.current = { qr: token, siteId: null, note: null, capturedAt: Date.now() };
            setStep({ name: "locating" });
          }}
          onManual={goManual}
        />
      );
      break;
    case "manual":
      body = (
        <ManualView
          kind={kind}
          me={me}
          geo={geo}
          onSubmit={(siteId, note) => {
            targetRef.current = { qr: null, siteId, note, capturedAt: Date.now() };
            setStep({ name: "locating" });
          }}
          onScan={retryScan}
        />
      );
      break;
    case "locating":
      body = (
        <div className="cl-sheet cl-centre stack">
          <Spinner label="Getting your location" />
          <h1>Getting your location…</h1>
          <p className="muted">This can take a few seconds indoors.</p>
          <GeoLine geo={geo} />
          <button className="btn block" onClick={submit}>
            Send now
          </button>
        </div>
      );
      break;
    case "sending":
      body = <SendingView startedAt={step.startedAt} what={what} />;
      break;
    case "result":
      body = (
        <ResultView
          kind={kind}
          response={step.response}
          onDone={close}
          onRetry={retryScan}
          onManual={goManual}
        />
      );
      break;
    case "blocked":
      body = (
        <BlockedView
          response={step.response}
          geo={geo}
          earlierNote={lastReqRef.current?.note ?? null}
          onRequestApproval={(note) => requestApproval(step.response, note)}
          onRetry={retryScan}
          onCancel={close}
        />
      );
      break;
    case "queued":
      body = (
        <SavedView
          what={what}
          step={step}
          onDone={close}
          onSendNow={() => void sendSavedNow()}
          onDiscardStuck={(s) => void discardStuck(s)}
          onDiscardThis={() => void discardThis()}
        />
      );
      break;
    case "error":
      body = (
        <div className="cl-sheet cl-centre stack">
          <div className="cl-badge-icon danger" aria-hidden="true">
            !
          </div>
          <h1>That didn't work</h1>
          <p className="cl-lead">{step.message}</p>
          {step.canRetry && lastReqRef.current && (
            <button className="btn primary huge" onClick={() => void deliver(lastReqRef.current!)}>
              Try again
            </button>
          )}
          <button className="btn block" onClick={close}>
            Close
          </button>
        </div>
      );
      break;
  }

  return (
    <div
      className={`cl-flow${step.name === "scan" ? " dark" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label={verb}
    >
      {header}
      <div className="cl-flow-body">{body}</div>
    </div>
  );
}

/** Shown while a punch is on its way; after a few seconds, says what happens if it's slow. */
function SendingView({ startedAt, what }: { startedAt: number; what: string }) {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    setSlow(false);
    const t = setTimeout(() => setSlow(true), Math.max(0, 6_000 - (Date.now() - startedAt)));
    return () => clearTimeout(t);
  }, [startedAt]);
  return (
    <div className="cl-sheet cl-centre stack">
      <Spinner label="Sending" />
      <h1>Sending…</h1>
      {slow && (
        <p className="muted" role="status">
          This is taking a while — the signal may be weak. If it doesn't get through, your {what} will be
          saved on this phone and sent later. You can also tap <strong>Stop waiting</strong> to save it now.
        </p>
      )}
    </div>
  );
}

function SavedView({
  what,
  step,
  onDone,
  onSendNow,
  onDiscardStuck,
  onDiscardThis,
}: {
  what: string;
  step: Extract<Step, { name: "queued" }>;
  onDone: () => void;
  onSendNow: () => void;
  onDiscardStuck: (stuck: FlushStop) => void;
  onDiscardThis: () => void;
}) {
  if (step.discarded) {
    return (
      <div className="cl-sheet cl-centre stack">
        <h1>Removed from this phone</h1>
        <p className="cl-lead">Your {what} won't be sent from this phone.</p>
        <p className="muted small">
          If it had already reached the office before you stopped waiting, it will still show. Ask the office
          to remove it if it's wrong.
        </p>
        <button className="btn primary huge" onClick={onDone}>
          Done
        </button>
      </div>
    );
  }
  const at = <strong>{fmtTime(step.at)}</strong>;
  const stuck = step.stuck;
  const stuckWhat = stuck?.item?.kind === "out" ? "clock-out" : "clock-in";
  const stuckAt = stuck?.item ? fmtTime(stuck.item.req.capturedAt ?? stuck.item.queuedAt) : null;
  let lead: ReactNode;
  switch (step.why) {
    case "offline":
    case "network":
      lead = (
        <>
          There's no signal right now. Your {what} at {at} is saved and will send by itself when you're back
          online.
        </>
      );
      break;
    case "timeout":
      lead = (
        <>
          The office didn't answer in time (the signal may be weak). Your {what} at {at} is saved and will
          send by itself. If it did get through, it won't be counted twice.
        </>
      );
      break;
    case "cancelled":
      lead = (
        <>
          Stopped waiting. Your {what} at {at} is saved on this phone and will send by itself. It may already
          have reached the office — either way it will only be counted once.
        </>
      );
      break;
    case "server":
      lead = (
        <>
          The office's system couldn't take your {what} at {at} just now
          {stuck?.message ? `: ${asSentence(stuck.message)}` : "."} It's saved on this phone and will be tried
          again by itself.
        </>
      );
      break;
    case "stuck":
      lead = (
        <>
          An earlier {stuckWhat}
          {stuckAt ? ` from ${stuckAt}` : ""} saved on this phone couldn't be sent yet
          {stuck?.message ? `: ${asSentence(stuck.message)}` : "."} Your {what} at {at} is saved behind it,
          and both will be sent by themselves.
        </>
      );
      break;
  }
  return (
    <div className="cl-sheet cl-centre stack">
      <div className="cl-badge-icon info" aria-hidden="true">
        ⟳
      </div>
      <h1>Saved on this phone</h1>
      <p className="cl-lead">{lead}</p>
      {step.why === "stuck" || step.why === "server" ? (
        <p className="muted small">If this keeps happening, please tell the office.</p>
      ) : (
        <p className="muted small">
          Just open the app when you have signal. Please don't delete the app until it has been sent.
        </p>
      )}
      <button className="btn primary huge" onClick={onDone}>
        Done
      </button>
      {step.why !== "offline" && (
        <button className="btn block" onClick={onSendNow}>
          Try sending now
        </button>
      )}
      {step.why === "stuck" && stuck?.item && (
        <button className="btn ghost block" onClick={() => onDiscardStuck(stuck)}>
          Remove the earlier {stuckWhat} and send this one
        </button>
      )}
      {step.why === "cancelled" && (
        <button className="btn ghost block" onClick={onDiscardThis}>
          Don't send it
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function locationProblem(geo: GeoSnapshot): LocationProblem | null {
  if (geo.fix) return geo.fix.accuracy > POOR_ACCURACY_M ? "imprecise" : null;
  if (geo.errorCode === GEO_DENIED) return "denied";
  if (geo.finished) return "unavailable";
  return null;
}

/** One-line location status, with "how to fix" help when there's a problem. */
function GeoLine({ geo, onDark }: { geo: GeoSnapshot; onDark?: boolean }) {
  const problem = locationProblem(geo);
  let text: string;
  let tone: "ok" | "wait" | "bad";
  if (geo.fix && !problem) {
    text = geo.fix.accuracy <= SEND_NOW_ACCURACY_M ? "Location found" : "Location found — improving…";
    tone = "ok";
  } else if (problem === "denied") {
    text = "Location is turned off";
    tone = "bad";
  } else if (problem === "imprecise") {
    text = "Your location isn't precise";
    tone = "bad";
  } else if (problem === "unavailable") {
    text = "Couldn't find your location";
    tone = "bad";
  } else {
    text = "Finding your location…";
    tone = "wait";
  }
  return (
    <div className={`cl-geo ${tone}${onDark ? " on-dark" : ""}`}>
      <div className="cl-geo-line" role="status">
        <span className="cl-geo-dot" aria-hidden="true" />
        {text}
      </div>
      {problem && <LocationHelp problem={problem} compact />}
    </div>
  );
}

type CameraState = "starting" | "live" | CameraProblem | "scanner_failed";

function ScanView({
  geo,
  onCode,
  onManual,
}: {
  geo: GeoSnapshot;
  onCode: (token: string) => void;
  onManual: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [camera, setCamera] = useState<CameraState>("starting");
  const [wrongCode, setWrongCode] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const wrongTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onCodeRef = useRef(onCode);
  onCodeRef.current = onCode;
  const cameraRef = useRef(camera);
  cameraRef.current = camera;

  // biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` restarts the camera on "Try again"
  useEffect(() => {
    const abort = new AbortController();
    let stream: MediaStream | null = null;
    const video = videoRef.current;
    setCamera("starting");
    // Load the decoder while the camera permission prompt is showing.
    const detectorP = getQrDetector();
    detectorP.catch(() => {});

    void (async () => {
      try {
        const s = await openRearCamera();
        if (abort.signal.aborted) {
          stopStream(s);
          return;
        }
        stream = s;
        streamRef.current = s;
        // The camera can be taken away (another app, or the phone locking): offer Try again.
        for (const track of s.getVideoTracks()) {
          track.addEventListener("ended", () => {
            if (!abort.signal.aborted && document.visibilityState === "visible") setCamera("other");
          });
        }
        if (!video) return;
        video.srcObject = s;
        await video.play().catch(() => {});
        setCamera("live");
        let detector: Awaited<typeof detectorP>;
        try {
          detector = await detectorP;
        } catch {
          stopStream(stream);
          if (!abort.signal.aborted) setCamera("scanner_failed");
          return;
        }
        await scanVideo(
          video,
          detector,
          (raw) => {
            const token = parseClockCode(raw);
            if (!token) {
              setWrongCode(true);
              if (wrongTimer.current) clearTimeout(wrongTimer.current);
              wrongTimer.current = setTimeout(() => setWrongCode(false), 2500);
              return false;
            }
            // Got it: release the camera straight away.
            abort.abort();
            stopStream(stream);
            navigator.vibrate?.(60);
            onCodeRef.current(token);
            return true;
          },
          abort.signal,
        );
      } catch (e) {
        if (abort.signal.aborted) return;
        if (e instanceof ScannerError) {
          // The decoder itself is broken (e.g. its download failed): load it afresh on Try again.
          resetQrDetector();
          stopStream(stream);
          setCamera("scanner_failed");
          return;
        }
        setCamera(e instanceof CameraError ? e.problem : "other");
      }
    })();

    return () => {
      abort.abort();
      stopStream(stream);
      streamRef.current = null;
      if (video) video.srcObject = null;
      if (wrongTimer.current) clearTimeout(wrongTimer.current);
    };
  }, [attempt]);

  // Back from the phone's Settings (where the help sends them to allow the camera), or
  // back from another app: reopen the camera if it was refused or has been taken away.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      const state = cameraRef.current;
      const ended = streamRef.current?.getVideoTracks().some((t) => t.readyState === "ended") ?? false;
      if (state === "denied" || state === "in_use" || state === "other" || (state === "live" && ended)) {
        setAttempt((n) => n + 1);
      } else if (state === "live") {
        void videoRef.current?.play().catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  const failed = camera !== "starting" && camera !== "live";

  return (
    <div className="cl-scan">
      <div className="cl-viewfinder">
        <video ref={videoRef} playsInline muted autoPlay aria-label="Camera view" />
        {!failed && <div className="cl-frame" aria-hidden="true" />}
        {camera === "starting" && (
          <div className="cl-scan-overlay">
            <Spinner label="Starting camera" />
            <p>Starting the camera…</p>
            <p className="small">
              If your phone asks to use the camera, tap <strong>Allow</strong>.
            </p>
          </div>
        )}
        {failed && (
          <div className="cl-scan-overlay cl-scan-failed">
            {camera === "scanner_failed" ? (
              <div className="cl-help stack">
                <h2>The scanner didn't load</h2>
                <p>Check your signal and try again.</p>
              </div>
            ) : (
              <CameraHelp problem={camera as CameraProblem} />
            )}
            <button className="btn primary block" onClick={() => setAttempt((n) => n + 1)}>
              Try again
            </button>
          </div>
        )}
      </div>
      <div className="cl-scan-panel">
        {camera === "live" && (
          <p className={`cl-scan-hint${wrongCode ? " wrong" : ""}`} role="status">
            {wrongCode ? "That's not one of our site codes" : "Point the camera at the code sticker"}
          </p>
        )}
        <GeoLine geo={geo} onDark />
        <button className="btn block cl-on-dark-btn" onClick={onManual}>
          Can't scan the code?
        </button>
      </div>
    </div>
  );
}

function ManualView({
  kind,
  me,
  geo,
  onSubmit,
  onScan,
}: {
  kind: PunchKind;
  me: Me;
  geo: GeoSnapshot;
  onSubmit: (siteId: string | null, note: string | null) => void;
  onScan: () => void;
}) {
  const [siteId, setSiteId] = useState<string | null>(() =>
    kind === "in" && me.sites.length === 1 ? me.sites[0]!.id : null,
  );
  const [note, setNote] = useState("");
  const needsSite = kind === "in";
  const noSites = needsSite && me.sites.length === 0;

  return (
    <form
      className="cl-sheet stack"
      onSubmit={(e) => {
        e.preventDefault();
        if (needsSite && !siteId) return;
        onSubmit(needsSite ? siteId : null, note.trim() || null);
      }}
    >
      <h1>{kind === "in" ? "Clock in without scanning" : "Clock out without scanning"}</h1>
      <p className="muted">
        Only use this if the code sticker is missing or damaged. The office may need to approve it.
      </p>
      {needsSite ? (
        noSites ? (
          <div className="banner warn">You're not linked to any sites yet. Please ask the office.</div>
        ) : (
          <fieldset className="cl-sites">
            <legend>Which site are you at?</legend>
            {me.sites.map((s) => (
              <label key={s.id} className={`cl-site-option${siteId === s.id ? " selected" : ""}`}>
                <input
                  type="radio"
                  name="site"
                  value={s.id}
                  checked={siteId === s.id}
                  onChange={() => setSiteId(s.id)}
                />
                <span>
                  <strong>{s.name}</strong>
                  {(s.address || s.postcode) && (
                    <span className="small muted cl-block">
                      {[s.address, s.postcode].filter(Boolean).join(", ")}
                    </span>
                  )}
                </span>
              </label>
            ))}
          </fieldset>
        )
      ) : (
        <p>
          Clocking out of <strong>{me.openVisit?.siteName ?? "your site"}</strong>.
        </p>
      )}
      <label className="field">
        <span>What happened? (optional)</span>
        <textarea
          value={note}
          maxLength={500}
          placeholder="For example: the sticker is missing"
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
      <GeoLine geo={geo} />
      <button className="btn primary huge" disabled={noSites || (needsSite && !siteId)}>
        {kind === "in" ? "Clock in" : "Clock out"}
      </button>
      <button type="button" className="btn ghost block" onClick={onScan}>
        Scan the code instead
      </button>
    </form>
  );
}

function ResultView({
  kind,
  response,
  onDone,
  onRetry,
  onManual,
}: {
  kind: PunchKind;
  response: Answered;
  onDone: () => void;
  onRetry: () => void;
  onManual: () => void;
}) {
  if (response.status === "rejected") {
    return (
      <div className="cl-sheet cl-centre stack">
        <div className="cl-badge-icon danger" aria-hidden="true">
          ✕
        </div>
        <h1>That didn't work</h1>
        <p className="cl-lead">{response.message}</p>
        <button className="btn primary huge" onClick={onRetry}>
          Scan again
        </button>
        <button className="btn block" onClick={onManual}>
          Can't scan the code?
        </button>
        <button className="btn ghost block" onClick={onDone}>
          Cancel
        </button>
      </div>
    );
  }
  const { visit, flags } = response;
  const punch = kind === "in" ? visit.in : (visit.out ?? visit.in);
  const pending = response.status === "pending";
  const notes = plainFlags(flags);
  return (
    <div className="cl-sheet cl-centre stack">
      <div className={`cl-badge-icon ${pending ? "info" : "ok"}`} aria-hidden="true">
        {pending ? "…" : "✓"}
      </div>
      <h1>{pending ? "Sent for approval" : kind === "in" ? "Clocked in" : "Clocked out"}</h1>
      <p className="cl-lead">
        <strong>{visit.siteName}</strong>
        <br />
        <span className="cl-result-time">{fmtTime(punch.at)}</span>
      </p>
      {kind === "out" && visit.minutes != null && !pending && (
        <p>
          You worked <strong>{fmtDuration(visit.minutes)}</strong>.
        </p>
      )}
      {pending && <p>Your time is saved. The office will check it and approve it.</p>}
      {notes.length > 0 && (
        <div className={`banner ${pending ? "info" : "warn"} cl-left`}>
          <strong>{pending ? "Why it needs approval:" : "Noted for the office:"}</strong>
          <ul className="cl-flag-list">
            {notes.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </div>
      )}
      <button className="btn primary huge" onClick={onDone}>
        Done
      </button>
    </div>
  );
}

function BlockedView({
  response,
  geo,
  earlierNote,
  onRequestApproval,
  onRetry,
  onCancel,
}: {
  response: Blocked;
  geo: GeoSnapshot;
  /** What they wrote on "Can't scan": sent along with this note. */
  earlierNote: string | null;
  onRequestApproval: (note: string) => void;
  onRetry: () => void;
  onCancel: () => void;
}) {
  const [note, setNote] = useState("");
  return (
    <div className="cl-sheet stack">
      <div className="cl-centre stack">
        <div className="cl-badge-icon warn" aria-hidden="true">
          !
        </div>
        <h1>Not clocked yet</h1>
        <p className="cl-lead">{response.message}</p>
      </div>
      {response.reason === "location_unavailable" && (
        <LocationHelp problem={locationProblem(geo) ?? "imprecise"} compact />
      )}
      <label className="field">
        <span>
          {earlierNote ? "Anything to add for the office? (optional)" : "Note for the office (optional)"}
        </span>
        <textarea
          value={note}
          maxLength={MAX_NOTE}
          placeholder="For example: I'm inside the basement"
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
      {earlierNote && (
        <p className="small muted">
          Your earlier note will be sent too: <q>{earlierNote}</q>
        </p>
      )}
      <button className="btn primary huge" onClick={() => onRequestApproval(note)}>
        Request approval
      </button>
      <p className="small muted cl-centre-text">Your time will be saved and the office will check it.</p>
      <button className="btn block" onClick={onRetry}>
        Try again
      </button>
      <button className="btn ghost block" onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}
